import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { 
  Calendar, Clock, MapPin, ChevronRight, ChevronLeft,
  Home, Users, FileText, CheckCircle, AlertTriangle,
  Navigation, ArrowRight
} from "lucide-react";

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const SHORT_DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function MySchedule() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState("today"); // today, tomorrow, week
  const [loading, setLoading] = useState(true);
  const [todaySchedule, setTodaySchedule] = useState(null);
  const [tomorrowSchedule, setTomorrowSchedule] = useState(null);
  const [weekSchedule, setWeekSchedule] = useState(null);
  const [nextVisit, setNextVisit] = useState(null);

  useEffect(() => {
    loadScheduleData();
  }, []);

  const loadScheduleData = async () => {
    setLoading(true);
    try {
      const [todayRes, tomorrowRes, weekRes, nextRes] = await Promise.all([
        api.get("/evv/schedule/today"),
        api.get("/evv/schedule/tomorrow"),
        api.get("/evv/schedule/week"),
        api.get("/evv/schedule/next-visit"),
      ]);
      setTodaySchedule(todayRes.data);
      setTomorrowSchedule(tomorrowRes.data);
      setWeekSchedule(weekRes.data);
      setNextVisit(nextRes.data);
    } catch (err) {
      console.error("Failed to load schedule:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + "T00:00:00");
    return date.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });
  };

  const formatShortDate = (dateStr) => {
    const date = new Date(dateStr + "T00:00:00");
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
      case "clocked_out":
        return "bg-emerald-50 text-emerald-700 border-emerald-200";
      case "in_progress":
      case "clocked_in":
        return "bg-blue-50 text-blue-700 border-blue-200";
      case "scheduled":
      case "confirmed":
        return "bg-gray-100 text-gray-600 border-gray-200";
      case "running_late_reported":
        return "bg-amber-50 text-amber-700 border-amber-200";
      case "missed":
      case "no_show":
        return "bg-red-50 text-red-700 border-red-200";
      default:
        return "bg-gray-100 text-gray-600 border-gray-200";
    }
  };

  const openMaps = (address) => {
    const encodedAddress = encodeURIComponent(address);
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, "_blank");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading schedule...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <header className="bg-[#06372B] text-white px-4 py-5 sticky top-0 z-40">
        <div className="flex items-center gap-3 max-w-lg mx-auto">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-full hover:bg-white/10">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="font-semibold text-lg">My Schedule</div>
            <div className="text-sm text-white/70">
              {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-5 space-y-5">
        {/* View Tabs */}
        <div className="flex bg-white rounded-xl p-1 shadow-sm border border-gray-100">
          {["today", "tomorrow", "week"].map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-colors ${
                view === v
                  ? "bg-[#06372B] text-white"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
              data-testid={`schedule-tab-${v}`}
            >
              {v === "today" ? "Today" : v === "tomorrow" ? "Tomorrow" : "Week"}
            </button>
          ))}
        </div>

        {/* Next Visit Card (always visible if exists) */}
        {nextVisit?.has_next_visit && (
          <div className="bg-gradient-to-br from-[#06372B] to-[#0a4a3a] rounded-2xl p-4 text-white" data-testid="next-visit-card">
            <div className="flex items-center gap-2 text-white/80 text-sm mb-2">
              <ArrowRight className="w-4 h-4" />
              <span>Next Visit{nextVisit.is_today ? " - Today" : ""}</span>
            </div>
            <div className="font-semibold text-lg">{nextVisit.client_name}</div>
            {nextVisit.client?.address && (
              <div className="text-white/80 text-sm mt-1 flex items-start gap-1">
                <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                <span className="line-clamp-1">{nextVisit.client.address}</span>
              </div>
            )}
            <div className="flex items-center gap-2 mt-3">
              {nextVisit.visit?.scheduled_start_time && (
                <div className="flex items-center gap-1 text-sm bg-white/20 rounded-full px-3 py-1">
                  <Clock className="w-3.5 h-3.5" />
                  {nextVisit.visit.scheduled_start_time}
                </div>
              )}
              {nextVisit.client?.address && (
                <button
                  onClick={() => openMaps(nextVisit.client.address)}
                  className="flex items-center gap-1 text-sm bg-white/20 rounded-full px-3 py-1 hover:bg-white/30 transition-colors"
                  data-testid="navigate-next-visit"
                >
                  <Navigation className="w-3.5 h-3.5" />
                  Navigate
                </button>
              )}
            </div>
          </div>
        )}

        {/* Today View */}
        {view === "today" && todaySchedule && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">
                {formatDate(todaySchedule.date)}
              </h2>
              <span className="text-sm text-gray-500">
                {todaySchedule.total_visits} visit{todaySchedule.total_visits !== 1 ? "s" : ""}
              </span>
            </div>

            {todaySchedule.visits?.length === 0 && todaySchedule.scheduled_shifts?.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 text-center border border-gray-100">
                <Calendar className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <div className="font-medium text-gray-900">No Visits Today</div>
                <div className="text-sm text-gray-500 mt-1">Enjoy your day off!</div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden divide-y divide-gray-100">
                {/* Show visits first */}
                {todaySchedule.visits?.map((visit) => (
                  <Link
                    key={visit.id}
                    to={`/employee/visit/${visit.id}`}
                    className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                    data-testid={`visit-${visit.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-gray-900">{visit.client_name}</div>
                      {visit.client?.address && (
                        <div className="text-sm text-gray-500 mt-0.5 flex items-start gap-1">
                          <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                          <span className="line-clamp-1">{visit.client.address}</span>
                        </div>
                      )}
                      {visit.scheduled_start_time && (
                        <div className="text-sm text-gray-500 mt-0.5 flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {visit.scheduled_start_time} - {visit.scheduled_end_time || "TBD"}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(visit.display_status || visit.status)}`}>
                        {(visit.display_status || visit.status).replace(/_/g, " ")}
                      </span>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </Link>
                ))}
                {/* Show scheduled shifts that don't have visits yet */}
                {todaySchedule.scheduled_shifts?.filter(s => 
                  !todaySchedule.visits?.some(v => v.scheduled_shift_id === s.id)
                ).map((shift) => (
                  <div
                    key={shift.id}
                    className="flex items-center justify-between p-4"
                    data-testid={`shift-${shift.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-gray-900">{shift.client_name}</div>
                      {shift.client?.address && (
                        <div className="text-sm text-gray-500 mt-0.5 flex items-start gap-1">
                          <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                          <span className="line-clamp-1">{shift.client.address}</span>
                        </div>
                      )}
                      <div className="text-sm text-gray-500 mt-0.5 flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {shift.scheduled_start_time} - {shift.scheduled_end_time}
                      </div>
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(shift.status)}`}>
                      {shift.status.replace(/_/g, " ")}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Tomorrow View */}
        {view === "tomorrow" && tomorrowSchedule && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">
                {formatDate(tomorrowSchedule.date)}
              </h2>
              <span className="text-sm text-gray-500">
                {tomorrowSchedule.total_shifts} shift{tomorrowSchedule.total_shifts !== 1 ? "s" : ""}
              </span>
            </div>

            {tomorrowSchedule.scheduled_shifts?.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 text-center border border-gray-100">
                <Calendar className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <div className="font-medium text-gray-900">No Shifts Scheduled</div>
                <div className="text-sm text-gray-500 mt-1">Check back later for updates.</div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden divide-y divide-gray-100">
                {tomorrowSchedule.scheduled_shifts?.map((shift) => (
                  <div
                    key={shift.id}
                    className="p-4"
                    data-testid={`tomorrow-shift-${shift.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-gray-900">{shift.client_name}</div>
                        {shift.client?.address && (
                          <div className="text-sm text-gray-500 mt-0.5 flex items-start gap-1">
                            <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                            <span className="line-clamp-1">{shift.client.address}</span>
                          </div>
                        )}
                        <div className="text-sm text-gray-500 mt-0.5 flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {shift.scheduled_start_time} - {shift.scheduled_end_time}
                        </div>
                      </div>
                      {shift.client?.address && (
                        <button
                          onClick={() => openMaps(shift.client.address)}
                          className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                          data-testid={`navigate-${shift.id}`}
                        >
                          <Navigation className="w-4 h-4 text-gray-600" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Week View */}
        {view === "week" && weekSchedule && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">
                Week of {formatShortDate(weekSchedule.week_start)}
              </h2>
            </div>

            <div className="space-y-3">
              {weekSchedule.schedule?.map((day) => {
                const hasActivity = day.shifts?.length > 0 || day.visits?.length > 0;
                const isToday = day.date === new Date().toISOString().split("T")[0];
                
                return (
                  <div
                    key={day.date}
                    className={`bg-white rounded-xl border overflow-hidden ${
                      isToday ? "border-[#06372B] ring-1 ring-[#06372B]/20" : "border-gray-100"
                    }`}
                    data-testid={`week-day-${day.date}`}
                  >
                    <div className={`px-4 py-3 border-b ${
                      isToday ? "bg-[#06372B]/5 border-[#06372B]/10" : "bg-gray-50 border-gray-100"
                    }`}>
                      <div className="flex items-center justify-between">
                        <div className="font-medium text-gray-900">
                          {day.day_name}
                          {isToday && (
                            <span className="ml-2 text-xs bg-[#06372B] text-white px-2 py-0.5 rounded-full">
                              Today
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">{formatShortDate(day.date)}</div>
                      </div>
                    </div>
                    
                    {!hasActivity ? (
                      <div className="p-4 text-center text-sm text-gray-500">
                        No shifts scheduled
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {/* Combined visits and shifts */}
                        {[...(day.visits || []), ...(day.shifts || [])].map((item, idx) => (
                          <div key={item.id || idx} className="p-3 flex items-center justify-between">
                            <div className="min-w-0 flex-1">
                              <div className="font-medium text-sm text-gray-900">{item.client_name}</div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                {item.scheduled_start_time} - {item.scheduled_end_time}
                              </div>
                            </div>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${
                              getStatusColor(item.display_status || item.status)
                            }`}>
                              {(item.display_status || item.status || "scheduled").replace(/_/g, " ")}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-2 z-40">
        <div className="max-w-lg mx-auto flex items-center justify-around">
          <Link 
            to="/employee/dashboard" 
            className="flex flex-col items-center gap-1 py-2 px-3 text-gray-400 hover:text-[#06372B] transition-colors"
          >
            <Home className="w-5 h-5" />
            <span className="text-xs font-medium">Home</span>
          </Link>
          <Link 
            to="/employee/schedule" 
            className="flex flex-col items-center gap-1 py-2 px-3 text-[#06372B]"
          >
            <Calendar className="w-5 h-5" />
            <span className="text-xs font-medium">Schedule</span>
          </Link>
          <Link 
            to="/employee/clients" 
            className="flex flex-col items-center gap-1 py-2 px-3 text-gray-400 hover:text-[#06372B] transition-colors"
          >
            <Users className="w-5 h-5" />
            <span className="text-xs font-medium">Clients</span>
          </Link>
          <Link 
            to="/employee/visits" 
            className="flex flex-col items-center gap-1 py-2 px-3 text-gray-400 hover:text-[#06372B] transition-colors"
          >
            <Clock className="w-5 h-5" />
            <span className="text-xs font-medium">Visits</span>
          </Link>
        </div>
      </nav>
    </div>
  );
}
